/**
 * Ficha do instrutor: o estado numa frase e três blocos (pode dar instrução?, horas, revalidações).
 *
 * Tudo vem do que o servidor já calcula para cada instrutor (`apto`, `hours`, `uniformizacao`,
 * `pu_revalidation`, `instructor_revalidation`, `status`), na data do cálculo (a simulação "data de cálculo" do
 * painel continua a funcionar): nunca se usa o relógio do navegador para os prazos.
 *
 * Tons: "ok" (verde), "warn" (âmbar), "bad" (vermelho).
 */

const WARN_DAYS = 45;      // mesma janela âmbar do servidor (AMBER_WINDOW_DAYS)
const REVALIDATE_WARN_DAYS = 90;
const MIN_HOURS_12M_MIN = 15 * 60;

export const fmtHM = (min) => {
  const m = Math.max(0, Math.round(Number(min) || 0));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
};

export function fmtDMY(iso) {
  return iso && String(iso).length >= 10 ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(0, 4)}` : "—";
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const daysWord = (n) => plural(n, "dia", "dias");

/** Tom de uma validade: vermelho se já passou ou falta registo, âmbar nos últimos 45 dias. */
function validityTone(days, hasDate) {
  if (!hasDate || days == null) return "bad";
  if (days < 0) return "bad";
  if (days <= WARN_DAYS) return "warn";
  return "ok";
}

function validityDetail(iso, days) {
  if (!iso) return "sem registo";
  if (days != null && days < 0) return `expirou há ${daysWord(-days)}`;
  return `até ${fmtDMY(iso)}`;
}

const QUAL_LABEL = {
  instr_teorica: "Instrutor de Teóricas",
  instr_de_instrutores: "Instrutor de Instrutores",
  examinador: "Examinador",
};

// Cor do progresso das 25 h de instrução (só a cor; o estado do instrutor não muda por causa disto).
// Antes da janela e enquanto o aviso não começou: azul. Janela aberta e horas por fazer: o nível do servidor
// (6, 3 e 1 mês antes da validade). Cumprido: verde.
function instructionTone(rev) {
  if (!rev || rev.window_phase === "no_validity") return null;
  if (rev.window_phase === "not_open") return "info";
  if (rev.ok) return "ok";
  if (rev.level === "critical") return "bad";
  if (rev.level === "warn") return "warn";
  return "info";
}

export function buildInstructorView(i) {
  const apto = i?.apto || {};
  const c = apto.criteria || {};
  const hours = i?.hours || {};
  const med = c.medical || {};
  const lic = c.instructor_license || {};
  const pu = c.pu_license || {};
  const quals = c.qualifications || {};
  const h12 = c.hours_15h_12m || {};
  const uniform = i?.uniformizacao || null;
  const puRev = i?.pu_revalidation || null;
  const instrRev = i?.instructor_revalidation || null;
  const instrTone = instructionTone(instrRev);

  // ── Condições para dar instrução (as 5 do servidor) ─────────────────────
  const conditions = [];
  const reasonOf = {};   // chave → frase do motivo (para o resumo do cartão)

  const addValidity = (key, label, crit, reasonLabel, short) => {
    const tone = validityTone(crit.days_remaining, !!crit.valid_until);
    const d = crit.days_remaining;
    conditions.push({
      key, label, short, detail: validityDetail(crit.valid_until, crit.days_remaining), tone,
      value: crit.valid_until ? fmtDMY(crit.valid_until) : "sem registo",
      sub: !crit.valid_until ? "falta registar a validade" : d < 0 ? `expirou há ${daysWord(-d)}` : `${daysWord(d)} restantes`,
    });
    if (tone === "bad") reasonOf[key] = !crit.valid_until ? `${reasonLabel}: sem validade registada` : `${reasonLabel} expirou há ${daysWord(-crit.days_remaining)}`;
    else if (tone === "warn") reasonOf[key] = `${reasonLabel} acaba em ${daysWord(crit.days_remaining)}`;
  };
  addValidity("medical", "Certificado médico", med, "Certificado médico", "Cert. médico");
  addValidity("instructor_license", "Licença de instrutor", lic, "Licença de instrutor", "Lic. instrutor");
  addValidity("pu", "Licença PU", pu, "Licença PU", "Lic. PU");

  // Qualificações: um mosaico por qualificação (são coisas diferentes, cada uma com a sua validade);
  // basta uma válida para poder dar instrução
  const allQuals = quals.all || [];
  const anyValid = quals.ok !== false && (quals.valid_count ?? (quals.valid || []).length ?? 0) > 0;
  const qualKeys = [];
  if (allQuals.length === 0) {
    conditions.push({ key: "qualification", label: "Qualificação", short: "Qualificação", detail: "nenhuma registada", tone: "bad", value: "Nenhuma", sub: "sem qualificação registada" });
    reasonOf.qualification = "Sem qualificação válida";
    qualKeys.push("qualification");
  } else {
    allQuals.forEach((q) => {
      const key = `qualification:${q.type}`;
      const name = QUAL_LABEL[q.type] || q.type;
      const d = q.days_remaining;
      let tone;
      let sub;
      if (q.ok) {
        tone = d != null && d <= WARN_DAYS ? "warn" : "ok";
        sub = d == null ? "sem prazo de validade" : tone === "warn" ? `acaba em ${daysWord(d)} (${fmtDMY(q.valid_until)})` : `até ${fmtDMY(q.valid_until)}`;
      } else {
        // Uma qualificação que falhou não impede dar instrução se houver outra válida: fica a aviso, não a erro
        tone = anyValid ? "warn" : "bad";
        sub = d != null && d < 0 ? `expirou há ${daysWord(-d)}` : (q.reason || "não válida");
      }
      conditions.push({ key, label: `Qualificação: ${name}`, short: "Qualificação", detail: sub, tone, value: name, sub });
      qualKeys.push(key);
      if (tone === "bad") reasonOf[key] = `Sem qualificação válida («${name}» ${sub})`;
      else if (tone === "warn") reasonOf[key] = q.ok ? `Qualificação «${name}» acaba em ${daysWord(d)}` : `Qualificação «${name}»: ${sub}`;
    });
  }

  // Horas nos últimos 12 meses (mínimo 15 h)
  const have12 = h12.value_min ?? hours.last12m_total_min ?? 0;
  const need12 = h12.required_min ?? MIN_HOURS_12M_MIN;
  const hoursOk = h12.ok ?? have12 >= need12;
  const buffer = have12 - need12;
  const hoursTone = !hoursOk ? "bad" : buffer < 3 * 60 ? "warn" : "ok";
  conditions.push({
    key: "hours", label: "Horas, 12 meses", short: "Horas 12 m", detail: `${fmtHM(have12)} de ${fmtHM(need12)}`, tone: hoursTone,
    value: fmtHM(have12),
    sub: !hoursOk ? `mín. ${fmtHM(need12)} · faltam ${fmtHM(need12 - have12)}` : `mín. ${fmtHM(need12)} · folga ${fmtHM(buffer)}`,
  });
  if (hoursTone === "bad") reasonOf.hours = `Só tem ${fmtHM(have12)} nos últimos 12 meses (mínimo ${fmtHM(need12)}): faltam ${fmtHM(need12 - have12)}`;
  else if (hoursTone === "warn") reasonOf.hours = `Folga de só ${fmtHM(buffer)} acima das ${fmtHM(need12)} mínimas dos últimos 12 meses`;

  // ── Revalidações (uma linha cada, com o caminho por extenso) ───────────────
  const revalidations = [];

  // Licença PU: 3 h e 10 aterragens nos 6 meses antes da validade
  {
    const days = pu.days_remaining;
    let tone = validityTone(days, !!pu.valid_until);
    let progress = "";
    let pct = null;
    let rule = pu.valid_until ? `3 h e 10 aterragens nos 6 meses antes de ${fmtDMY(pu.valid_until)}` : "Sem validade registada: não se sabe quando revalidar";
    if (puRev && puRev.window_phase !== "no_validity") {
      const met = puRev.state === "apto";
      if (puRev.window_phase === "not_open") {
        progress = `A janela abre a ${fmtDMY(puRev.window_start)}`;
        tone = tone === "bad" ? "bad" : "ok";
      } else {
        const miss = [];
        if (puRev.missing_minutes > 0) miss.push(fmtHM(puRev.missing_minutes));
        if (puRev.missing_flights > 0) miss.push(plural(puRev.missing_flights, "aterragem", "aterragens"));
        progress = met
          ? `Já cumpre: ${fmtHM(puRev.total_minutes)} e ${plural(puRev.landings_count ?? puRev.flights_count, "aterragem", "aterragens")}`
          : `Tem ${fmtHM(puRev.total_minutes)} e ${plural(puRev.landings_count ?? puRev.flights_count, "aterragem", "aterragens")}: faltam ${miss.join(" e ")}`
            + (puRev.state === "confirmar_aterragens" ? " (ou confirmar as aterragens)" : "");
        pct = Math.min(100, Math.round(100 * Math.min(puRev.total_minutes / puRev.required_minutes, (puRev.landings_count ?? puRev.flights_count) / puRev.required_flights)));
        if (days != null && days >= 0 && !met) tone = days <= WARN_DAYS ? "bad" : days <= REVALIDATE_WARN_DAYS ? "warn" : "ok";
        else if (days != null && days >= 0) tone = "ok";
      }
    }
    revalidations.push({ key: "pu", title: "Licença PU", tone, rule, progress, pct, days, validUntil: pu.valid_until, target: "edit" });
    if (tone !== "ok") reasonOf.pu_revalidation = reasonOf.pu_revalidation || (days != null && days < 0 ? "Licença PU expirada" : `Revalidação da PU por fazer (acaba em ${daysWord(days)})`);
  }

  // Licença de instrutor: 25 h de instrução nos 12 meses antes da validade, OU prova com examinador
  {
    const days = lic.days_remaining;
    let tone = validityTone(days, !!lic.valid_until);
    let progress = "";
    let pct = null;
    const rule = lic.valid_until ? `25 h de instrução nos 12 meses antes de ${fmtDMY(lic.valid_until)}, OU prova com examinador` : "Sem validade registada: não se sabe quando revalidar";
    if (instrRev && instrRev.window_phase !== "no_validity") {
      if (instrRev.window_phase === "not_open") {
        progress = `${fmtHM(instrRev.instr_minutes)} de ${fmtHM(instrRev.required_minutes)} · a janela abre a ${fmtDMY(instrRev.window_start)}`;
        pct = Math.min(100, Math.round((100 * instrRev.instr_minutes) / instrRev.required_minutes));
        tone = tone === "bad" ? "bad" : "ok";
      } else {
        progress = instrRev.ok
          ? `Já cumpre: ${fmtHM(instrRev.instr_minutes)} de instrução`
          : `Tem ${fmtHM(instrRev.instr_minutes)}: faltam ${fmtHM(instrRev.missing_minutes)}, ou marcar a prova${!instrRev.level && instrRev.level_from ? ` · o aviso começa a ${fmtDMY(instrRev.level_from)}` : ""}`;
        pct = Math.min(100, Math.round((100 * instrRev.instr_minutes) / instrRev.required_minutes));
        if (days != null && days >= 0 && !instrRev.ok) tone = days <= WARN_DAYS ? "bad" : days <= REVALIDATE_WARN_DAYS ? "warn" : "ok";
        else if (days != null && days >= 0) tone = "ok";
      }
    }
    // Clicar nesta linha mostra os registos de instrução da caderneta que a conta usa (instruction_records, no servidor)
    revalidations.push({
      key: "instructor_license", title: "Licença de instrutor", tone, rule, progress, pct, days, validUntil: lic.valid_until, target: "records",
      progressTone: instrTone,
      instr: instrRev ? {
        records: instrRev.records || [], totalMin: instrRev.instr_minutes ?? 0, requiredMin: instrRev.required_minutes ?? 0,
        windowStart: instrRev.window_start || null, windowEnd: instrRev.window_end || null,
      } : null,
    });
    if (tone !== "ok") reasonOf.instructor_revalidation = reasonOf.instructor_revalidation || (days != null && days < 0 ? "Licença de instrutor expirada" : `Revalidação da licença de instrutor por fazer (acaba em ${daysWord(days)})`);
  }

  // Certificado médico
  revalidations.push({
    key: "medical", title: "Certificado médico", tone: validityTone(med.days_remaining, !!med.valid_until),
    rule: med.valid_until ? `Renovar no médico antes de ${fmtDMY(med.valid_until)}` : "Sem validade registada", progress: "", pct: null,
    days: med.days_remaining, validUntil: med.valid_until, target: "edit",
  });

  // Uniformização
  if (uniform) {
    const never = uniform.status === "never";
    const tone = never ? "ok" : uniform.status === "ok" ? "ok" : uniform.status === "amber" ? "warn" : "bad";
    revalidations.push({
      key: "uniformizacao", title: "Uniformização", tone,
      rule: never ? "Sem voo de uniformização registado" : `Próxima a ${fmtDMY(uniform.next_due)}`, progress: "", pct: null,
      days: never ? null : uniform.days_remaining, validUntil: never ? null : uniform.next_due, target: "log", neutral: never,
    });
    if (tone !== "ok") reasonOf.uniformizacao = uniform.days_remaining != null && uniform.days_remaining < 0 ? `Uniformização em atraso há ${daysWord(-uniform.days_remaining)}` : `Uniformização a ${fmtDMY(uniform.next_due)}`;
  }

  // Qualificações com validade
  (quals.all || []).filter((q) => q.valid_until).forEach((q) => {
    revalidations.push({
      key: `qual:${q.type}`, title: QUAL_LABEL[q.type] || q.type, tone: q.ok ? validityTone(q.days_remaining, true) : "bad",
      rule: q.ok ? `Válida até ${fmtDMY(q.valid_until)}` : (q.reason || "Não válida"), progress: "", pct: null,
      days: q.days_remaining, validUntil: q.valid_until, target: "edit",
    });
  });

  // ── Estado numa frase ──────────────────────────────────────────────────────
  const isInactive = i?.active === false;
  const order = ["medical", "instructor_license", "pu", ...qualKeys, "hours"];
  const badKeys = order.filter((k) => conditions.find((x) => x.key === k)?.tone === "bad");
  // Avisos: condições âmbar, revalidações por fazer e uniformização em falta; cada causa conta uma vez
  const warnKeys = new Set();
  conditions.filter((x) => x.tone === "warn").forEach((x) => warnKeys.add(x.key));
  if (reasonOf.pu_revalidation && !badKeys.includes("pu")) warnKeys.add("pu");
  if (reasonOf.instructor_revalidation && !badKeys.includes("instructor_license")) warnKeys.add("instructor_license");
  if (reasonOf.uniformizacao) warnKeys.add("uniformizacao");
  if (apto.apto !== false && i?.status === "amber" && warnKeys.size === 0) warnKeys.add("generic");

  const REASON_KEYS = { pu: ["pu", "pu_revalidation"], instructor_license: ["instructor_license", "instructor_revalidation"] };
  const reasonFor = (key) => (REASON_KEYS[key] || [key]).map((k) => reasonOf[k]).find(Boolean) || "Ver detalhes";
  const warnOrder = ["medical", "instructor_license", "pu", ...qualKeys, "hours", "uniformizacao", "generic"];
  const warnList = warnOrder.filter((k) => warnKeys.has(k));

  let kind;
  let phrase;
  let reason;
  if (isInactive) {
    kind = "inactive"; phrase = "Desativado"; reason = "Instrutor desativado: não conta para os avisos";
  } else if (apto.apto === false || badKeys.length > 0) {
    kind = "grounded"; phrase = "Não pode dar instrução";
    const first = badKeys[0] || "hours";
    reason = reasonFor(first) + (badKeys.length > 1 ? ` (+${badKeys.length - 1})` : "");
  } else if (warnList.length > 0) {
    kind = "warn"; phrase = `Pode dar instrução, com ${plural(warnList.length, "aviso", "avisos")}`;
    const k = warnList[0];
    reason = (k === "generic" ? "Ver detalhes" : reasonFor(k)) + (warnList.length > 1 ? ` (+${warnList.length - 1})` : "");
  } else {
    kind = "ok"; phrase = "Pode dar instrução"; reason = "Tudo em dia";
  }

  // ── Horas ──────────────────────────────────────────────────────────────────
  const metrics = [
    { label: "Total, piloto", value: fmtHM(hours.total_min) },
    { label: "Total, instrução", value: fmtHM(hours.instr_min) },
    { label: "12 meses, piloto", value: fmtHM(hours.last12m_total_min) },
    { label: "12 meses, instrução", value: fmtHM(hours.last12m_instr_min), tone: instrTone },
  ];
  const initial = (hours.initial_pic_min || 0) > 0 || (hours.initial_instructor_min || 0) > 0
    ? { pic: fmtHM(hours.initial_pic_min), instr: fmtHM(hours.initial_instructor_min) } : null;

  // ── Linha do tempo (próximos 12 meses): só o que tem data ─────────────────────
  const timeline = revalidations
    .filter((r) => r.validUntil && r.days != null && r.days <= 365 && !r.neutral)
    .map((r) => ({
      key: r.key, days: Math.max(0, r.days), overdue: r.days < 0, tone: r.tone, date: r.validUntil,
      label: ({ pu: "PU", instructor_license: "Instrutor", medical: "Médico", uniformizacao: "Uniformiz." })[r.key] || r.title,
      detail: `${r.title}: ${r.days < 0 ? `expirou há ${daysWord(-r.days)}` : `a ${fmtDMY(r.validUntil)} (${daysWord(r.days)})`}`,
    }))
    .sort((a, b) => a.days - b.days);

  // ── Dados secundários (visíveis pequenos; em destaque ao abrir) ────────────────
  const info = {
    medicalNumber: i?.medical_cert_number || null,
    licenseIssuedAt: quals.license_issued_at || null,
    years: quals.years_as_instructor ?? null,
  };

  // Instrutor restrito (R): progresso para perder a restrição (vem do servidor, só existe quando é R)
  const rs = i?.r_to_s;
  const restricted = rs && rs.applicable ? {
    instrMin: rs.instr_min ?? 0, requiredInstrMin: rs.required_instr_min ?? 50 * 60,
    solos: rs.solos_count ?? 0, requiredSolos: rs.required_solos ?? 10,
    reportOk: !!rs.report_ok, reportApprovedAt: rs.report_approved_at || null, allOk: !!rs.all_ok,
  } : null;

  return {
    kind, phrase, reason, conditions, revalidations, metrics, initial, timeline, info, restricted,
    hours12: {
      have: have12, need: need12, pct: Math.min(100, Math.round((100 * have12) / (need12 || 1))), ok: hoursOk,
      records: i?.hours_records || [], window: i?.hours_window || null,
    },
    attentionCount: warnList.length,
  };
}
